import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import RevisionRequestManager from '../../StateControllers/Survey/RevisionRequestManager';
import {
    DataCollectionAiAgentOperationType,
    ExtensionRequestModel
} from '../../StateControllers/Survey/SurveyResponse/SurveyResponseModels';

// Re-export DataCollectionAiAgentOperationType for use in other components
export { DataCollectionAiAgentOperationType };
import { DynamicRegulation } from '../Compliance/DynamicRegulation';
import { FileTableEntry } from '../Documents/DocumentModels';
import { SurveyCampaignTableEntry } from '../Api/Surveys/SurveyCampaignModels';
import { InviteSupplierModel, InviteSupplierRowModel } from '../Supplier/Supplier';
import { AiMetadataProperties } from './SurveyAiMetadata';

/*
 userName: first + last name of user
 sendingEmailAddress: connected email (if microsoft email/gmail is connected) or same as emailAddress (used for visual display only)
 emailAddress: email address associated with account
 */
export interface VerifiedSenderAddresses {
    userName: string,
    sendingEmailAddress: string
    emailAddress: string
}

export interface SurveyInventorySummaryMetadata {
    rowSummaryContent: SummaryRowContent[];
    hasLinkedMaterials: boolean;
}

export interface SummaryRowContent {
    columnId: string;
    value: string;
}

export enum SurveyStatusState {
    Sent = 0,
    InProgress = 1,
    ResponseReceived = 2,
    AllAccepted = 3,
    Draft = 4,
    PartiallyAccepted = 5,
    RevisionSent = 6,
    RevisonInProgress = 7,
    RevisionResponseReceived = 8,
    RequestInitiated = 9,
    RevisionRequestInitiated = 10,
    CustomerAction = 11,
    Escalated = 12,
}

export interface SurveyReminder {
    ownerId: string | null;
    requestId: string | null;
    reminderId: string | null;
    languageToReminderMessageMap: { [language: string]: string };
    scheduledDaysSinceRequestSent: number;
    scheduledSendDate: number;
    state: string;
    peekLockTimeout: number;
    scheduledSendDateUnix: number
}

export interface QuestionnaireAnswerThatMakesBomDataRequiredSetting {
    pageTitle: string;
    questionText: string;
    nativeAnswer: string;
}

export interface RevisionDetails {
    revisionInstructions: string;
    pageIndex: number;
    questionIndex: number;
    columnId: string;
}

export interface QuestionRevision {
    revisionInstructions: string;
    pageIndex: number;
    questionIndex: number;
}

export interface BomColumnRevision {
    revisionInstructions: string;
    columnId: string;
}

export interface GeneralRevisionRequest {
    generalRevisionInstructions: string;
    dateSentIso8601: string | null;
    dateRevisionResponseReceivedIso8601: string | null;
    sentTo: string | null;
}

export interface SupplierQuestionnaireRevisionRequest extends GeneralRevisionRequest {
    supplierQuestionRevisions: QuestionRevision[];
}

export interface BomRevisionRequest extends GeneralRevisionRequest {
    responseInventoryId: string;
    bomQuestionnaireQuestionRevisions: QuestionRevision[];
    attestationQuestionnaireQuestionRevisions: QuestionRevision[];
    bomColumnRevisions: BomColumnRevision[];
    sustainabilityRevisionInstructions: string;
    circularityRevisionInstructions: string;
    vocRevisionInstructions: string;
    dynamicRegulationRevisionInstructions: { [dynamicRegulationId: string]: string };
}

export interface SurveyHistoryEntryGranularRevisionRequest extends GeneralRevisionRequest {
    supplierQuestionnaireRevisionRequest: SupplierQuestionnaireRevisionRequest | null;
    bomRevisionRequestTable: { [responseInventoryId: string]: BomRevisionRequest };
}

export interface SurveyRequestInventoryMetadata {
    inventoryId: string;
    customizedName: string;
    originalName: string;
}

export interface SurveyOfflineResponseData {
    supplierResponseText: string;
    notes: string;
    fileIds: string[];
    dateLastUpdated: string; // ISO8601 formatted date time
}

export interface SupplierQuestionnaireData {
    dateAccepted: string | null; // ISO8601
    questionnaire: Questionnaire;
    revisionRequest: SupplierQuestionnaireRevisionRequest | null;
    files: FileTableEntry[] | null | undefined;
}

export enum SurveyResponseSource {
    SupplierResponse = 0,
    OfflineResponse = 1,
    OfflineResponseWithAi = 2,
    SupplierWithAi = 3,
    SourceNotAvailable = 4
}

export type SurveyResponseSourceValue = SurveyResponseSource | keyof typeof SurveyResponseSource;

export interface SurveyRequestResponseHistoryInventoryData {
    responseInventoryId: string;
    plausibilityStatus?: string; // Plausibility Status for survey material (optional)
    responsePublicInventoryId: string | null;
    responseSharedMaterialId: string | null;
    targetInventoryId: string | null;
    dateAccepted: string | null; // null if not accepted
    revisionRequest: BomRevisionRequest | null;
}

export interface SurveyRequestResponseHistoryEntry {
    historyId: string;
    dateReceived: string; // ISO8601
    dateAccepted: string | null; // ISO8601, or null if never accepted
    supplierQuestionnaireData: SupplierQuestionnaireData | null;
    inventoryDataList: SurveyRequestResponseHistoryInventoryData[];
    offlineResponseData: SurveyOfflineResponseData | null; // null if no data
    source: SurveyResponseSourceValue | null;
    revisionEmailText: string;
    dateRevisionSent: string | null; // ISO8601
    dateRevisionResponseReceived: string | null; // ISO8601
    dateRevisionCancelled: string | null; // ISO8601
    revisionCancellationReason: string | null;
    revisionCancellationInitiatedBy: string | null;
    cancelledRevisionMaterials: string[];
}

export interface SurveyRequestNote {
    ownerId: string | null;
    requestId: string | null;
    dateTimePosted: string;
    posterName: string;
    posterEmail: string;
    note: string;
}

// ===============================================================================

export enum SurveySendStateType {
    None,
    SendingSurvey,
    SendSurveyFailed,
    SendSurveyContainsLinkedMaterials,
    SendSurveySucceeded,
    SendSurveyContainsInvalidFromEmaillAddress,
    SendSurveyContainsInvalidCcEmailList,
    SendSurveyRefreshTokenFailed
}

export enum OverallSurveyType {
    BomRelated = "BomRelated",
    SupplierOnly = "SupplierOnly"
}

export enum SaveTemplateMode {
    Default,
    SaveDraftsOnContinue,
    SendSurveysOnContinue
}

//State corresponding to how the Survey UI displays
export interface SurveyUi {
    currentPage: number;
    currentLanguage: string;
    currentState: SurveyMode;
    currentSection: DisplaySection;
    showCustomName: boolean;
    showErrorMessage: boolean;
    dataLoaded: boolean;
    showDataSaveMessage: boolean;
    sentReminderAlert: boolean;
    errorReminderNoMessageAlert: boolean;
    errorReminderAlert: boolean;
    sendReminderMessage: string;
    showErrorScreen: boolean;
    showNotFoundErrorScreen: boolean;
    surveySendStateType: SurveySendStateType;
    responseHistoryListIsExpanded: boolean;
    revisionRequestManager: RevisionRequestManager | null;
    offlineResponseSupplierResponseInputText: string;
    offlineResponseNotesInputText: string;
    offlineResponseAttachments: FileList | null;
    addOfflineResponseSuccess: boolean | null;
    sendGranularRevisionRequestMode: boolean;
    viewGranularRevisionRequestHistoryEntryId: string | null;
    surveyHasRequestsThatAreRedundantWithCustomQuestionnaire: boolean;
    showAdvancedIngredientColumns: boolean;
    activeSendOfflineProcessId: string;
    activeAcceptOfflineProcessId: string;
    showMaterialsTables: boolean;
    showMissingSupplierMessage: boolean;
    currentTemplateId: string | null;
    saveTemplateMode: SaveTemplateMode;
    newTemplateName: string;
    saveTemplateAnyRadioSelected: boolean;
    saveTemplateSelectedTemplateId: string | null;
}

export enum DisplaySection {
    MaterialDisclosure = "MaterialDisclosure",
    ComplianceData = "ComplianceData",
    SustainabilityData = "SustainibilityData",
    CircularityData = "CircularityData",
    VocData = "VocData",
    SupplierInfo = "SupplierInfo",
    DocumentsCertifications = "DocumentsCertification",
    CustomQuestions = "CustomQuestion",
    None = "None"
}

export enum SurveyMode {
    New = 0,
    EditExisting = 1,
    Template
}

export enum QuestionValidationType {
    None = 0,
    Guid = 1
}

export interface QuestionnairePredefinedAnswer {
    nativeAnswer: string;
    languageToAnswerMap: { [language: string]: string };
}

export interface QuestionnaireQuestion {
    text: string;
    dynamicField: string;
    languageToTextMap: { [language: string]: string };
    type: string;
    value: string;
    valueComment: string;
    requireFileAttachment: boolean;
    predefinedAnswers: QuestionnairePredefinedAnswer[];
    nextPageIndexMap: { [nativeAnswer: string]: number };
    fileIds: string[];
    id: string;
    isRequired: boolean;
    instructions: string;
    languageToInstructionsMap: { [language: string]: string };
    conditionalOnQuestion: {
        pageType: QuestionnairePageType | null;
        questionIndex: number;
        answers: string[];
        requireOnlyIfParentIsRequired: boolean;
    };
    feedbackMessage: {
        text: string;
        answers: string[];
    };
    questionValidation: {
        validationType: QuestionValidationType;
        validationMessage: string;
    }
    summaryFieldKey: string | null;
    aiMetadata: AiMetadataProperties | null;
    summaryFieldKeyInventoryFileType: number | null;
    summaryFieldQuestionValueToSummaryTableValueMap: { [k: string]: string };
    summaryFieldMultiCheckboxIndexToKeyList: string[];
    nativeAnswerToMultipleSummaryKeyMap: { [k: string]: string };
    metadataPageKey: string;
    metadataFieldKey: string;
    scipDataKey: string;
    disabledValues: string[];
    hidden: boolean;
    subType: string;
}

export enum QuestionnaireProcessingType {
    Legacy = "Legacy",
    Dynamic = "Dynamic",
    Custom_BOM = "Custom_BOM",
    Custom_Attestation = "Custom_Attestation",
    Supplier = "Supplier",
    Scip = "Scip"
}

export enum SurveyPageType {
    Instructions,
    SupplierQuestionnaire,
    AddMaterials,
    GroupInventories,
    Inventory,
    Bom,
    Batch
}

// TODO: KYLE REFCHECK
export enum QuestionnairePageType {
    Custom = "Custom",
    CaProp65 = "CaProp65",
    ConflictMinerals = "ConflictMinerals",
    ConflictMineralsV2 = "ConflictMineralsV2",
    Red2Green = "Red2Green",
    REACHCompliance = "REACHCompliance",
    RoHSCompliance = "RoHSCompliance",
    SDS = "SDS",
    LEEDv4 = "LEEDv4",
    CradleToCradle = "CradleToCradle",
    ReachRohsStatement = "ReachRohsStatement",
    Teknion = "Teknion",
    GeneralCustomUI = "GeneralCustomUI",
    HPDCustomUI = "HPDCustomUI",
    Declare = "Declare",
    ReachAuthorisationCompliance = "ReachAuthorisationCompliance",
    ScipCustomUI = "ScipCustomUI",
    ScipQuestionnaire = "ScipQuestionnaire",
    HniEds = "HniEds",
    Sustainability = "Sustainability",
    SustainabilityWaterUse = "SustainabilityWaterUse",
    SustainabilityEmbodiedCarbon = "SustainabilityEmbodiedCarbon",
    SustainabilityAssessment = "SustainabilityAssessment",
    SustainabilityTransparency = "SustainabilityTransparency",
    SustainabilityOptimization = "SustainabilityOptimization",
    Circularity = "Circularity",
    WaterUse = "WaterUse",
    EmbodiedCarbon = "EmbodiedCarbon",
    DocumentsAndCertifications = "DocumentsAndCertifications",
    SDSCustomUI = "SDSCustomUI",
    DynamicRegulation = "DynamicRegulation",
    Attestation = "Attestation",
    BOM = "BOM",
    VocAll = "VocAll",
    VocTransparency = "VocTransparency",
    VocAssessment = "VocAssessment",
    VocOptimization = "VocOptimization",
    ReachAuthorisationV2Compliance = "ReachAuthorisationV2Compliance",
}

export interface QuestionnairePage {
    title: string;
    languageToTitleMap: { [language: string]: string };
    defaultNextPageIndex: number;
    type: QuestionnairePageType;
    questions: QuestionnaireQuestion[];
    regulationId?: string;
    hasPageBeenVisited: boolean;
    nextClicked?: boolean;
    questionRegulatoryData: QuestionRegulatoryData[] | null;
    regulatoryListIds: string[];
}

export interface QuestionRegulatoryData {
    regulationId: string;
    applicableQuestionId: string;
    containsSubstancesResponse: string;
    doesNotContainSubstancesResponse: string;
    regulationListIds: string[];
}

export interface ComplianceRegulationDetails {
    regulationId: string;
    regulationListIds: string[];
}

export interface Questionnaire {
    questionnaireId: string;
    formatVersion: number;
    languageList: string[];
    nativeLanguage: string;
    name: string;
    languageToNameMap: { [language: string]: string };
    instructions: string;
    languageToInstructionsMap: { [language: string]: string };
    type: number;
    showBranding: boolean;
    pages: QuestionnairePage[];
    inventoryIdList: string[];
}

export enum CompleteStatus {
    Complete = "Complete",
    Incomplete = "Incomplete",
    Error = "Error"
}

export enum SustainabilityAndCircularityIds {
    EmbodiedCarbon = "EmbodiedCarbon",
    WaterUsageData = "WaterUsageData",
    EndOfLifeOptions = "EndOfLifeOptions",
    Contact = "Contact",
    Instructions = "Instructions",
    CarbonTransparency = "SustainabilityTransparency",
    CarbonOther = "SustainabilityOther",
}

export enum VocIds {
    VocTransparency = "VocTransparency",
    VocOther = "VocOther",
}

export enum StandardRequestedDocumentType {
    HPD = "HPD",
    VOC = "VOC",
    C2C = "C2C"
}

export interface RequestedDocument {
    documentCertificationType: string;
    documentNameIfOther: string
}

export interface SurveyDataRequested {
    materialDisclosureAsked: boolean;
    complianceList: string[];
    circularityDataAsked: boolean;
    sustainabilityDataAsked: boolean;
    embodiedCarbonDataAsked: boolean;
    waterUseDataAsked: boolean;
    carbonTransparencyDataAsked: boolean;
    carbonOtherDataAsked: boolean;
    vocDataAsked: boolean;
    vocTransparencyDataAsked: boolean;
    vocOtherDataAsked: boolean;
    documentsCertificationList: RequestedDocument[];
    complianceDataAsked?: boolean,
    documentsAsked?: boolean,
    customQuestionsAsked?: boolean
    anyPassportDataAsked: boolean;
}

export interface SurveyData {
    verifiedEmailUsers: VerifiedSenderAddresses[];
    connectedEmailUsers: VerifiedSenderAddresses[];
    manufacturerLogoFileId: string | null;
    usersLanguage: string;
    requestableColumnIdToFriendlyNameMap: { [columnId: string]: string },
    surveyableColumns: SurveyableColumn[];
    inventoryIdToSummaryRowContentsMap: { [inventoryId: string]: SurveyInventorySummaryMetadata };
    columnIdsInSummaryRows: string[];
    questionnaireTable: { [questionnaireID: string]: Questionnaire };
    revisableHistoryEntryId: string;
    enterpriseSupplierSurveyEnabled: boolean;
    surveyResponsesThisYearQuotaUsage: number;
    defaultSurveyScheduledReminderHour: number;
    historyIdsThatAreUnderQuota: string[];
    offlineResponseFileTable: { [fileId: string]: OfflineResponseFile };
    responseInventoryMetadataTable: { [responseInventoryId: string]: SurveyResponseInventoryMetadata };
    templates: SurveyTemplateDataModel[];
    defaultSurveyTemplateId: string | null;
    campaigns: SurveyCampaignTableEntry[];
    surveysToSend: InviteSupplierModel[];
    dynamicRegulations: DynamicRegulation[];
    offlineProcessDataResponses: any[];
    offlineProcessQueuedMaterials: any[];
    extensionRequest: ExtensionRequestModel | null;
}

export function emptySurveyMetadata(): SurveyData {
    return {
        verifiedEmailUsers: [],
        connectedEmailUsers: [],
        manufacturerLogoFileId: null,
        usersLanguage: "en",
        requestableColumnIdToFriendlyNameMap: {},
        surveyableColumns: [],
        inventoryIdToSummaryRowContentsMap: {},
        columnIdsInSummaryRows: [],
        questionnaireTable: {},
        revisableHistoryEntryId: "",
        enterpriseSupplierSurveyEnabled: true,
        surveyResponsesThisYearQuotaUsage: 0,
        defaultSurveyScheduledReminderHour: 0,
        historyIdsThatAreUnderQuota: [],
        offlineResponseFileTable: {},
        responseInventoryMetadataTable: {},
        templates: [],
        defaultSurveyTemplateId: null,
        campaigns: [],
        surveysToSend: [],
        dynamicRegulations: [],
        offlineProcessDataResponses: [],
        offlineProcessQueuedMaterials: [],
        extensionRequest: null
    };
}

export enum SurveyType {
    RequestMaterialData_SpecificMaterials = "RequestMaterialData_SpecificMaterials",
    RequestMaterialData_AllowSupplierToSpecifyMaterials = "RequestMaterialData_AllowSupplierToSpecifyMaterials",
    JustSupplierQuestionnaire = "JustSupplierQuestionnaire",
    ConsolidateCertifications = "ConsolidateCertifications"
}

export interface SurveyResponseInventoryDataDTO {
    bomDataHasBeenAdded: boolean;
    inventoryValidationStatus: string | null;
    publicInventoryId: string;
    questionnaireHasBeenWalkedThrough: boolean;
    requestorOwnedResponseInventoryId: string;
    requestorTargetLibraryInventoryId: string;
    responderOwnedResponseInventoryId: string;
}

export interface SurveyResponse {
    responseId: string;
    requestOwnerId: string;
    requestId: string;
    state: number;
    lastSubmittedResponseWasPublished: boolean;
    responseDataJson: string | null;
    inventoryDataList: SurveyResponseInventoryDataDTO[];
    supplierQuestionnaireData: SupplierQuestionnaireData;
}

export interface CompliReminderRecord {
    sentDate: string; // iso8601
    message: string;
}

export interface SurveyRequestMetadata {
    requireSuppliersToDiscloseRegulatoryIngredients: boolean;
    additionalColumnIdIdentifiers: string[];
    compliReminderHistory?: CompliReminderRecord[] | null;
}

export interface SurveyRequest {
    ownerId: string | null;
    requestId: string | null;
    surveyTemplateId: string | null;
    campaignId: string | null;
    campaignName: string | null;
    surveyType: SurveyType;
    bomType: number; // 0: specific material data request, 1: open material data request, 2: only supplier-level info questionnaire request, 3: consolidate certifications request
    surveyName: string;
    tags: string[];
    dataRequested: SurveyDataRequested;
    fromName: string;
    fromEmailAddress: string;
    requestInventoryMetadataList: SurveyRequestInventoryMetadata[];
    activationCode: string;
    state: number; /// 0 = sent, 1 = in progress, 2 = response received, 3 = all accepted, 4 = draft, 5 = partially accepted, 6 = revision sent, 7 = revision in progress, 8 = revision response received, 9 = request initiated, 10 = revision request initiated
    workflowStatus: string;
    isInStateOfBeingWorkedOnBySupplier: boolean;
    supplierId: string | null;
    sendToName: string;
    sendToEmail: string;
    sendToCompany: string;
    acceptedByName: string;
    acceptedByEmail: string;
    dateSent: string | null; // iso8601
    requestorName: string;
    requestorEmail: string;
    metadataTemplateId?: string | null;
    bomQuestionnaireId: string | null;
    supplierQuestionnaireId: string | null;
    attestationQuestionnaireId: string | null;
    responseHistoryList: SurveyRequestResponseHistoryEntry[]
    isActivatable: boolean;
    responseOwnershipHistoryList: any[]; //TODO
    languageList: string[];
    nativeLanguage: string | null;
    ccEmailList: string[];
    emailSubject: string;
    emailMessage: string;
    entireEmailHtml: string;
    requestInstructions: string;
    completionInstructions: string;
    languageToEmailSubjectMap: { [cultureName: string]: string };
    languageToEmailMessageMap: { [cultureName: string]: string };
    languageToRequestInstructionsMap: { [cultureName: string]: string };
    languageToCompletionInstructionsMap: { [cultureName: string]: string };
    languageToSendIn: string | null;
    useManufacturerLogo: boolean;
    preFillBomQuestionnaireFromSummaryData: boolean;
    preFillSupplierQuestionnaireFromSummaryData: boolean;
    requireValidationOnResponse: boolean;
    autoAcceptEnabled: boolean;
    inventoryMetadataToOverwrite: any;
    scheduledReminders: SurveyReminder[];
    reminderHistory: string[]; // array of is8601 dates
    sdsRequested: boolean;
    questionnaireAnswerThatMakesBomDataRequired?: QuestionnaireAnswerThatMakesBomDataRequiredSetting | null;
    canSendReminderFor: boolean;
    numRemindersSent: number;
    dateLastReminder?: string; // iso 8601 date
    notes: SurveyRequestNote[];
    canBeRevised: boolean;
    mostRecentHistoryEntry?: any; //TODO
    makeBomDataRequiredOnQuestionnaireAnswer: boolean | null,
    fields: SurveyRequestedBomColumn[];
    openRequestMaterialNames: string[] | null;
    includeAttestationLetter: boolean;
    metadata: SurveyRequestMetadata;
    allowExtensionRequest: boolean;
    requestDataJson: string;
    bomTemplateConfiguration: BomColumnTemplateConfiguration | null;
}

export function emptySurveyRequest(
    usersLanguage: string,
    columnIdToDefaultColumnRequiredFlagsTable: Readonly<{ [columnId: string]: ColumnRequiredFlags }>
): SurveyRequest {
    return {
        ownerId: null,
        requestId: null,
        surveyTemplateId: null,
        campaignId: null,
        campaignName: null,
        surveyType: SurveyType.RequestMaterialData_SpecificMaterials,
        bomType: 0,
        surveyName: "",
        tags: [],
        dataRequested: {
            materialDisclosureAsked: false,
            circularityDataAsked: false,
            sustainabilityDataAsked: false,
            embodiedCarbonDataAsked: false,
            waterUseDataAsked: false,
            carbonTransparencyDataAsked: false,
            carbonOtherDataAsked: false,
            vocDataAsked: false,
            vocTransparencyDataAsked: false,
            vocOtherDataAsked: false,
            complianceList: [],
            documentsCertificationList: [],
            anyPassportDataAsked: false
        },
        fromName: "",
        fromEmailAddress: "",
        requestInventoryMetadataList: [],
        activationCode: "",
        state: 0,
        workflowStatus: "",
        isInStateOfBeingWorkedOnBySupplier: false,
        supplierId: null,
        sendToName: "",
        sendToEmail: "",
        sendToCompany: "",
        acceptedByName: "",
        acceptedByEmail: "",
        dateSent: null,
        requestorName: "",
        requestorEmail: "",
        metadataTemplateId: null,
        bomQuestionnaireId: null,
        supplierQuestionnaireId: null,
        attestationQuestionnaireId: null,
        responseHistoryList: [],
        isActivatable: false,
        responseOwnershipHistoryList: [],//TODO
        languageList: [usersLanguage],
        nativeLanguage: "",
        ccEmailList: [],
        emailSubject: "",
        emailMessage: "",
        entireEmailHtml: "",
        requestInstructions: "",
        completionInstructions: "",
        languageToEmailSubjectMap: {},
        languageToEmailMessageMap: {},
        languageToRequestInstructionsMap: {},
        languageToCompletionInstructionsMap: {},
        languageToSendIn: "",
        useManufacturerLogo: false,
        preFillBomQuestionnaireFromSummaryData: false,
        preFillSupplierQuestionnaireFromSummaryData: false,
        requireValidationOnResponse: false,
        autoAcceptEnabled: false,
        inventoryMetadataToOverwrite: {},//TODO
        scheduledReminders: [],
        reminderHistory: [],
        sdsRequested: false,
        questionnaireAnswerThatMakesBomDataRequired: null,
        canSendReminderFor: false,
        numRemindersSent: 0,
        notes: [],
        canBeRevised: false,
        makeBomDataRequiredOnQuestionnaireAnswer: false,
        fields: [
            {
                columnId: "level",
                ...(columnIdToDefaultColumnRequiredFlagsTable["level"])
            },
            {
                columnId: "part-name",
                ...(columnIdToDefaultColumnRequiredFlagsTable["part-name"])
            },
            {
                columnId: "casrn",
                ...(columnIdToDefaultColumnRequiredFlagsTable["casrn"])
            },
            {
                columnId: "percent-by-weight",
                ...(columnIdToDefaultColumnRequiredFlagsTable["percent-by-weight"])
            },
        ],
        openRequestMaterialNames: null,
        requestDataJson: "",
        includeAttestationLetter: false,
        metadata: {
            requireSuppliersToDiscloseRegulatoryIngredients: false,
            additionalColumnIdIdentifiers: []
        },
        allowExtensionRequest: true,
        bomTemplateConfiguration: null
    };
}
interface BomColumnTemplateConfiguration {
    extensibleColumns: string[];
}
// state of the survey
export interface SurveyState {
    surveyUi: SurveyUi;
    surveyRequest: SurveyRequest;
    surveyMetadata: SurveyData;
}

export type SurveyInvitationPreviewRequest = Pick<SurveyRequest,
    'requestId' | 'fromName' | 'emailSubject' | 'emailMessage' | 'requestInstructions' |
    'useManufacturerLogo' | 'nativeLanguage' | 'languageToSendIn' |
    'languageToEmailSubjectMap' | 'languageToEmailMessageMap' | 'languageToRequestInstructionsMap' |
    'dataRequested' | 'bomQuestionnaireId' | 'supplierQuestionnaireId'> & {
        supplierId: string | null;
        supplierName: string;
        materials: { inventoryId: string | null; name?: string; customizedName: string }[];
    };

export interface SurveyInvitationPreview {
    subject: string;
    html: string;
}

export interface SurveyRequestApiResponseBundle {
    surveyRequest: SurveyRequest;
    surveyData: SurveyData;
}

export interface SurveyRequestApiRequestBundle extends SurveyRequestApiResponseBundle {
    shouldSend?: boolean;
}

export interface SurveyResponseInventoryMetadata {
    inventoryValidationStatus: number;
    inventoryName: string;
    responseInventoryId: string;
    bomQuestionnaire: Questionnaire;
    attestationQuestionnaire: Questionnaire;
    targetInventoryId: string | null;
    sdsMetadata: SurveyResponseInventorySdsMetadata[];
    description: string;
    isTargetInventoryProtectLinked: boolean;
}
export interface SurveyResponseInventorySdsMetadata {
    fileId?: string | null; // TypeScript doesn't support Guid natively, so using string or null
    documentId: number;
    fileName: string;
    documentUrl: string;
}

export interface SurveyTemplateDataModel {
    ownerId: string | null;
    templateId: string | null;
    name: string;
    dataRequested: SurveyDataRequested;
    bomQuestionnaireId: string | null;
    supplierQuestionnaireId: string | null;
    attestationQuestionnaireId: string | null;
    languageList: string[];
    nativeLanguage: string | null;
    ccEmailList: string;
    emailSubject: string;
    emailMessage: string;
    requestInstructions: string;
    completionInstructions: string;
    languageToEmailSubjectMap: { [cultureName: string]: string };
    languageToEmailMessageMap: { [cultureName: string]: string };
    languageToRequestInstructionsMap: { [cultureName: string]: string };
    languageToCompletionInstructionsMap: { [cultureName: string]: string };
    scheduledReminders: SurveyReminder[];
    sdsRequested: boolean;
    questionnaireAnswerThatMakesBomDataRequired?: QuestionnaireAnswerThatMakesBomDataRequiredSetting | null;
    bomColumns: SurveyRequestedBomColumn[];
    useManufacturerLogo: boolean;
    preFillBomQuestionnaireFromSummaryData: boolean | null;
    preFillSupplierQuestionnaireFromSummaryData: boolean | null;
    makeBomDataRequiredOnQuestionnaireAnswer: boolean | null;
    requireSuppliersToDiscloseRegulatoryIngredients: boolean;
    additionalColumnIdIdentifiers: string[];
    includeAttestationLetter: boolean;
    lastModified: string | null;
    lastModifiedBy: string | null;
    lastModifiedByEmail: string | null;
}

export interface OfflineResponseApiData {
    requestId: string;
    supplierResponseText: string;
    notes: string;
    uploadedFileIds: string[]
}

// retrieved via localStorage.getItem("newSurveyData");
export interface GetSurveyMetadataRequest {
    startFromSurveyTemplateId: string | null;
    supplierIdsForSurvey: string[];
    inventoryIdsForSurvey: string[];
    mode: "bom" | "open-bom" | "supplier";
}

export interface SurveyRequestedBomColumn {
    columnId: string;
    columnName?: string | null;
    requiredInSummaryRow: boolean;
    requiredInLeafRows: boolean;
    requiredInNonSummaryNonLeafRows: boolean;
    // EXG-7722 (M2 / Jira 1499213): true when the persisted custom column can no longer
    // be resolved by ColumnName on the requestor's current BOM template (renamed,
    // deleted, or type-changed after the draft was saved). The picker should render a
    // visible warning and prompt the requestor to reselect; save/send must not proceed
    // until reconciled.
    isUnavailable?: boolean;
}

export interface SurveyableColumn {
    columnId: string;
    columnName?: string;
    pairedColumnId: string | null; // null when no column paired
    canRemove: boolean;
    isAdvancedColumn: boolean;
    isCustomColumn?: boolean;
    isFlagColumn?: boolean;
    isEnabled?: boolean;
}

export interface ColumnRequiredFlags {
    requiredInSummaryRow: boolean;
    requiredInNonSummaryNonLeafRows: boolean;
    requiredInLeafRows: boolean;
}

export interface OfflineResponseFile {
    filename: string
}

export class SurveyComplianceRegulationValues {
    public static getLocalizedFriendlyDescriptionMap(dynRegs: Readonly<DynamicRegulation[]>) {
        const map: { [key: string]: string } = {};

        for (let dynReg of dynRegs) {
            map[dynReg.regulationId] = dynReg.config.name;
        }

        return map;
    }

    public static getLocalizedDisplayNameMap(dynRegs: Readonly<DynamicRegulation[]>) {
        const map: { [key: string]: string } = {};

        for (let dynReg of dynRegs) {
            map[dynReg.regulationId] = dynReg.config.displayName?.length > 0 ? dynReg.config.displayName : dynReg.config.name;
        }

        return map;
    }
}

export class OfflineProcessAlertManagerValues {
    public static getProcessingTypeToFriendlyDescriptionMap(localizer: IStringLocalizer): { [key: string]: string } {
        return {
            "CreateAndSendSurveys": localizer.localizeString("Your survey is being sent."),
            "CreateAndSendSupplierInviteSurveys": localizer.localizeString("Your survey is being sent."),
            "SendDraftSurvey": localizer.localizeString("Your survey is being sent."),
            "ActivateSurveyMaterials": localizer.localizeString("Your survey is being activated."),
            "AcceptSurveyMaterials": localizer.localizeString("Your survey response is being accepted."),
            "SubmitSurveyResponse": localizer.localizeString("Your survey response is being submitted.")
        };
    }

    public static getProcessingTypeToFailedDescriptionMap(localizer: IStringLocalizer): { [key: string]: string } {
        return {
            "CreateAndSendSurveys": localizer.localizeString("A survey failed to send."),
            "CreateAndSendSupplierInviteSurveys": localizer.localizeString("A survey failed to send."),
            "SendDraftSurvey": localizer.localizeString("A survey failed to send."),
            "ActivateSurveyMaterials": localizer.localizeString("A survey failed to activate."),
            "AcceptSurveyMaterials": localizer.localizeString("A survey response failed to be accepted."),
            "SubmitSurveyResponse": localizer.localizeString("A survey response failed to be submitted.")
        };
    }

    public static getProcessingTypeToSuccessDescriptionMap(localizer: IStringLocalizer): { [key: string]: string } {
        return {
            "CreateAndSendSurveys": localizer.localizeString("Your survey has been sent."),
            "CreateAndSendSupplierInviteSurveys": localizer.localizeString("Your survey has been sent."),
            "SendDraftSurvey": localizer.localizeString("Your survey has been sent."),
            "ActivateSurveyMaterials": localizer.localizeString("Your survey has been activated."),
            "AcceptSurveyMaterials": localizer.localizeString("Your survey response has been accepted."),
            "SubmitSurveyResponse": localizer.localizeString("Your survey has been submitted.")
        };
    }
}

export enum OfflineProcessType {
    CreateAndSendSupplierInviteSurveys = "CreateAndSendSupplierInviteSurveys",
    ExportDashboard = "ExportDashboard",
    CreateAndSendOrSaveSurvey = "CreateAndSendOrSaveSurvey",
    SendDraftSurvey = "SendDraftSurvey",
    Report = "Report"
}

export enum OfflineProcessingStageType {
    Queued = "Queued",
    Working = "Working",
    TotallyComplete = "TotallyComplete",
    Failed = "Failed"
}

export enum QuestionType {
    MultipleDropdown = "multiple-dropdown",
    DataTable = "data-table",
    MultiCheckbox = "multi-checkbox",
    Attestation = "attestation",
    Multiple = "multiple",
    Text = "text",
    Comment = "comment",
    File = "file",
    Datetime = "date-time",
    ImageUrl = "image-url",
    Url = "url",
    MultiText = "multi-text",
    ScipArticleCategory = "scip-article-category",
    CsiDivision = "csi-division",
    ScipArticleIdentifier = "scip-article-identifier",
    IngredientTable = "ingredients-table",
    Certification = "certification",
    ComplianceResponseDate = "compliance-response-date",
    ComplianceResponseDateReach = "compliance-response-date-reach",
    ScipCharacteristics = "scip-characteristics",
    CheckboxAndComment = "checkbox-and-comment",
    CmrtComplianceAnswer = "cmrt-compliance-answer",
    CmrtCompanyQuestionAnswer = "cmrt-company-question-answer",
    CmrtDetails = "cmrt_details_json",
    CmrtMultiMaterialInput = "cmrt-multi-material-input",
    Subsection = "sub-section",
    DropdownMenuAndComment = "dropdown-menu-and-comment",
    CMRTMineralsComponent = "cmrt-minerals-component",
    PhoneContact = "phone-contact",
    Section = "section",
    ImportCMRT = "import-cmrt",
    SmelterList = "smelter-list",
    ProductList = "product-list",
}

export enum QuestionSubType {
    FDA = "fda-citation-data-table",
    FCN = "fcn-data-table",
    Polymer = "polymer-data-table",
    Packaging = "packaging-layer-data-table",
    PrintingTechnique = "printing-technique-data-table",
    ProductionSite = "production-site-data-table"
}

export interface ArticleIdentifier {
    identifierTypeId: string;
    value: string;
    typeOtherText: string;
}

interface SurveyResponseInventoryDataMetadata {
    questionnairePageIndicesVisited: number[];
    bomEditorPageVisited: boolean;
}

export interface SurveyResponseInventoryData {
    responderOwnedResponseInventoryId: string;
    requestorOwnedResponseInventoryId: string | null;
    requestorTargetLibraryInventoryId: string | null;
    publicInventoryId: string | null;
    sharedMaterialId: string | null;
    revisionRequest: BomRevisionRequest | null;
    questionnaireHasBeenWalkedThrough: boolean;
    bomDataHasBeenAdded: boolean;
    supplierLibraryInventoryId: string | null;
    metadata: SurveyResponseInventoryDataMetadata;
    inventoryValidationStatus: string | null;
}

export enum ResponseBomType {
    SuppliedBoms = "SuppliedBoms",
    Open = "Open",
    None = "None",
    CertificationConsolidation = "CertificationConsolidation"
}

// see: class DbSurveyResponseInventoryGroup
export interface SurveyResponseInventoryGroup {
    groupName: string;
    groupInventoryId: string | null;
    responderOwnedResponseInventoryIdList: string[];
    supplierLibraryInventoryId: string | null;
    publicInventoryId: string | null;
    sharedMaterialId: string | null;
}

export type GroupedResponseInventories = { [groupName: string]: SurveyResponseInventoryGroup };

export interface SurveyResponseOfflineProcessStatus {
    isOfflineProcessStarted: boolean;
}

export interface SurveyResponseModel {
    responseId: string;
    ownerId: string;
    requestOwnerId: string;
    requestId: string;
    state: number;
    lastSubmittedResponseWasPublished: boolean;
    responseDataJson: string | null;
    bomType: ResponseBomType;
    isRevisionResponse: boolean;
    supplierQuestionnaireData: SupplierQuestionnaireData | null;
    inventoryDataList: SurveyResponseInventoryData[];
    revisionReqInvNames: string[];
    companyName: string;
    groupedResponseInventories: GroupedResponseInventories;
    files: FileTableEntry[];
}

export class MetadataFieldTypes {
    // TODO: build out and consolidate the rest of the types
    static readonly MultipleDropdown = "multiple-dropdown";
    static readonly MultiText = "multi-text";
    static readonly DataTable = "data-table";
    static readonly IngredientsTable = "ingredients-table";
    static readonly Certification = "certification";
    static readonly CsiDivision = "csi-division";
    static readonly ScipArticleIdentifier = "scip-article-identifier";
    static readonly ScipArticleCategory = "scip-article-category";
    static readonly MultiCheckbox = "multi-checkbox";
    static readonly Attestation = "attestation";
    static readonly Subsection = "sub-section";
}

export interface InventoryRow {
    isExpanded: boolean;
    isVisible: boolean;
    inventoryId: string;
    rowIndex: number;
    rowNumber: number;
    level: number;
    linkedInventoryId: string;
    isReachArticle: boolean;
    missingLinkedComponent: boolean;
    isLinkedRow: boolean;
    assignedSubstanceId: string | null;
    assignedPrivateSubstanceId: string | null;
    deepProprietarySubstanceId: string | null;
    casNumber: string;
    chemicalName: string;
    privateCasNumber: string;
    privateChemicalName: string;
    privateHazardRating: string;
    privateHcodes: string;
    overrideCasNumber: string;
    overrideChemicalName: string;
    overrideHazardRating: string;
    overrideHcodes: string;
    overrideEcNumber: string;
    overallScore: string;
    substanceBiodegradabilityStatus: string;
    partName: string;
    description: string;
    supplier: string;
    userFacingSupplierId: string;
    externalDbId: string;
    weight: string;
    yield: string;
    unitOfMeasure: string;
    materialType: string;
    materialFamily: string;
    percentByWeight: string;
    componentPercentByWeight: string;
    wetComponentPercentByWeight: string;
    percentOfWetIngredientInFinalProduct: string;
    wetUnitWeight: string;
    wetWeight: string;
    wetOverallPercentByWeight: string;
    isProprietary: boolean;
    isDeepProprietary: boolean;
    nanomaterial: string;
    notes: string;
    plant: string;
    recycledContent: string;
    role: string;
    residualsAndImpuritiesEvaluation: string;
    residualsAndImpuritiesNotes: string;
    threshold: string;
    url: string;
    alternative: string;
    quantity: string;
    unitWeight: string;
    percentPostConsumerRecycledContent: string;
    percentPreConsumerRecycledContent: string;
    scrapRate: string;
    c2cTechnicalOrBiologicalNutrient: string;
    c2cRecyclable: string;
    c2cBiodegradable: string;
    c2cDisassemblability: string;
    c2cPercentRapidlyRenewableContent: string;
    c2cDescriptionOfRapidlyRenewableContent: string;
    c2cEmc: string;
    c2cNotes: string;
    dataSource: string;
    supplierPartNumber: string;
    reachNumber: string;
    pbtOrVpvb: string;
    clpClassification: string;
    versionDescription: string;
    scipConcernElementMaterialCategory: string;
    scipConcernElementMixtureCategory: string;
    serializedHpdSpecialCondition: string;
    extensibleColumns: string[];
    allListMetadataList: ListEntry[];
    inheritedAllListMetadataList: ListEntry[];
    inheritedScore: string;
    inheritedSubstanceBiodegradabilityStatus: string | null; // can be null
    ecNumbers: string[];
    privateEcNumber: string;
    relativePercentIsCalculated: boolean;
    overallPercentIsCalculated: boolean;
    weightIsCalculated: boolean;
    unitWeightIsCalculated: boolean;
    wetWeightIsCalculated: boolean;
    wetRelativePercentIsCalculated: boolean;
    wetOverallPercentByWeightIsCalculated: boolean;
    percentOfWetIngredientInFinalProductIsCalculated: boolean;
    percentPreConsumerRecycledIsCalculated: boolean;
    percentPostConsumerRecycledIsCalculated: boolean;
    recycledContentTotalIsCalculated: boolean;
    c2cRecyclableIsCalculated: boolean;
    scrapRateIsCalculated: boolean;
    linkedInventorySummaryData: { [field: string]: string };
    c2cRenewableContentIsCalculated: boolean;
    productNumber: string;
    scipSubmissionId: string;
    otherStandardColumnsTable: { [columnId: string]: string };
    aiMetadata: AiMetadataProperties | null;
}

export interface ListEntry {
    listId: string;
    listType: number;
    substanceId: string;
    listName: string;
    casNumber: string;
    reasonForInclusion: string;
}

export enum AlertType {
    Green,
    Red,
    Gray
}

export interface IngredientsAlertModel {
    text: string;
    alertType: AlertType;
}

// ===============================================================================

export enum ScipCharacteristicType {
    Height = "Height",
    Length = "Length",
    Width = "Width",
    Diameter = "Diameter",
    Density = "Density",
    Weight = "Weight",
    Volume = "Volume",
    Colour = "Colour",
    Other = "Other"
}

// ===============================================================================

export interface ScipCharacteristic {
    type: ScipCharacteristicType;
    typeOtherText: string
    value: string;
    unitId: string;
}

// ===============================================================================

enum ScipSubmissionStateType {
    Draft = "Draft",
    SubmittedPending = "SubmittedPending",
    SubmittedAccepted = "SubmittedAccepted",
    SubmittedRejected = "SubmittedRejected"
}

// ===============================================================================

export interface LinkedArticleType {
    submissionData: ScipSubmissionData;
    numberOfUnits: string;
}

// ===============================================================================

interface DisassemblingInstruction {
    toxnotFileId: string;
    languageCode: string;
}

// ===============================================================================

export interface MaterialCategoryType {
    categoryId: string;
    additionalCharacteristics: string[];
    typeOtherText: string;
}

// ===============================================================================

export interface ConcernElement {
    referenceSubstanceName: string;
    casNumber: string;
    casName: string; // substance/chemical name
    concentrationRange: {
        lowerBound: number;
        upperBound: number;
    };
    materialCategory: MaterialCategoryType;
    mixtureCategoryId: string;
}

// ===============================================================================

interface OtherNameType {
    typeId: string; // brand | model | type | other (text)
    name: string;
    typeOtherText: string;
}

// ===============================================================================

export interface ScipSubmissionData {
    scipSubmissionId: string;
    sourceInventoryId: string;
    sourceInventoryName: string;
    sourceInventoryDescription: string;
    state: ScipSubmissionStateType;
    dateSubmitted: string;
    submissionNumber: string;
    scipNumber?: string;
    oldScipNumber?: string;
    echaUserReportUrl: string;
    articleName: string;
    otherNames: OtherNameType[];
    primaryArticleIdentifier: ArticleIdentifier;
    otherArticleIdentifiers: ArticleIdentifier[];
    articleCategoryIds: string[];
    productionInEuropeanUnionId: string;
    characteristics: ScipCharacteristic[];
    safeUseInstructions: string;
    disassemblingInstructions: DisassemblingInstruction[];
    toxnotInventoryPhotoFileId: string; // null => no file specified
    linkedArticles: LinkedArticleType[];
    concernElements: ConcernElement[];
    noSafeUseInstructionRequired: boolean;
    isScipNumberManuallyEntered?: boolean;
    inventoryRowIndex: number | null;
    hasScipNumber: string | null;
}

export interface CustomColumn {
    customColumnName: string;
    customColumnIndex: number;
    customColumnType: QuestionType.Text | QuestionType.Datetime | QuestionType.MultipleDropdown;
    required?: boolean;
    validationMessage?: string;
    predefinedAnswers?: QuestionnairePredefinedAnswer[];
}

// Supplier AI Workflow Request Interface
export interface SupplierAiWorkflowRequest {
    requestId: string;
    fileIds: string[];
    isDataOverriden: boolean;
    operationType: DataCollectionAiAgentOperationType;
    autoAcceptSurveyResponse?: boolean;
    enableWebRetrieval?: boolean;
    webRetrievalUrls?: string[];
    userNotes?: string;
}

export interface AiDeleteAllAnswersStartRequest {
    requestId: string;
    operationId: string;
}

export interface AiTerminateRequest {
    requestId: string;
    operationId: string;
}

export interface AiQuestionHelpfulFeedbackRequest {
    responseId: string;
    processingType: QuestionnaireProcessingType | null;
    inventoryId: string | null;
    pageName: string | null;
    fieldText: string;
    summaryFieldKey: string | null;
    scipDataKey: string | null;
    isHelpful: number;
}

export interface AiBomHelpfulFeedbackRequest {
    responseId: string;
    inventoryId: string;
    isHelpful: number;
}

export interface AiBomHelpfulFeedbackResponse {
    isHelpful: number;
}


